import React, { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { useTranslate } from '@tolgee/react';
import { useNotify } from "../../Helpers/Notifications";
import MeetingList from "./MeetingList";
import PastMeetingList from "./PastMeetingList";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import { useAuthService } from "../../Context/AuthContext";
import MeetingDetailsCard from "./MeetingDetailsCard";
import { Link2, CalendarPlus, CalendarRange, Globe, Sparkles } from "lucide-react";
import IconButton from "../GenericComponents/IconButton";
import PrimaryButton from "../GenericComponents/PrimaryButton";
import { colors, rgb } from "../../Assets/styles/colors";
import { ACCESS_LEVEL_PLATFORM } from "../../Constants/constantValues";
import AddNewMeetingModal from "./AddNewMeetingModal";
import JoinMeetingModal from "./JoinMeetingModal";

const Meetings = ({
  meetings,
  pastMeetings,
  pastMeetingsHasMore,
  loadMeetingsHistory,
  handleJoinMeeting,
  handleSelectMeeting,
  loadMeetings,
  selectedMeeting,
  handleCloseMeetingDetails,
  handleDeleteMeetingBtn,
  handleUpdateMeetingDetails,
  handleRegeneratePasscode,
  contacts,
  checkSubscriptionAndExecute,
}) => {
  const [activeTab, setActiveTab] = useState("upcoming");
  const {
    isMeetingDetailsOpen,
    setIsAddNewMeetingOpen,
    isAddNewMeetingOpen,
    setIsMeetingDetailsOpen,
    isJoinMeetingByCodeOpen,
    setIsJoinMeetingByCodeOpen,
  } = useGenericComponentsStateService();
  const { offering } = useAuthService();
  const { notifyError } = useNotify();
  const theme = useTheme();
  const { t } = useTranslate();
  const handleOpenAddNewMeeting = () => {
    if (offering && offering.meetings !== true) {
      notifyError(t('misc.platform_error'));
      return;
    }
    const accessLevel = ACCESS_LEVEL_PLATFORM;
    checkSubscriptionAndExecute(accessLevel, () => {
      setIsAddNewMeetingOpen(true);
      setIsMeetingDetailsOpen(false);
    });
  };

  const handleOpenJoinMeetingByCode = () => {
    setIsJoinMeetingByCodeOpen(true);
    setIsMeetingDetailsOpen(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    handleCloseMeetingDetails();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      loadMeetings();
    }, 60000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (isMeetingDetailsOpen && selectedMeeting) {
      const stillExists = [...meetings, ...pastMeetings].some(
        (m) => m.id === selectedMeeting.id
      );
      if (!stillExists) {
        handleCloseMeetingDetails();
      }
    }
  }, [meetings, pastMeetings]);

  return (
    <Container>
      <Main>
        <Header>
          <HeaderTitle>{t('general.meetings')}</HeaderTitle>
          <HeaderActions>
            <IconButton
              icon={<Link2 size={20} />}
              title={t('meetings.join_with_meeting_id')}
              onClick={() => handleOpenJoinMeetingByCode()}
            />
            <IconButton
              icon={<CalendarPlus size={20} />}
              title={t('meetings.create_a_meeting')}
              onClick={() => handleOpenAddNewMeeting()}
            />
          </HeaderActions>
        </Header>
        <Tabs>
          <TabButton
            $active={activeTab === "upcoming"}
            onClick={() => handleTabChange("upcoming")}
          >
            {t('meetings.tab_upcoming')}
          </TabButton>
          <TabButton
            $active={activeTab === "past"}
            onClick={() => handleTabChange("past")}
          >
            {t('meetings.tab_past')}
          </TabButton>
        </Tabs>
        {activeTab === "upcoming" ? (
          <MeetingList
            meetings={meetings}
            handleJoinMeeting={handleJoinMeeting}
            handleSelectMeeting={handleSelectMeeting}
            loadMeetings={loadMeetings}
            selectedMeeting={selectedMeeting}
          />
        ) : (
          <PastMeetingList
            meetings={pastMeetings}
            hasMore={pastMeetingsHasMore}
            loadMeetingsHistory={loadMeetingsHistory}
            handleJoinMeeting={handleJoinMeeting}
            handleSelectMeeting={handleSelectMeeting}
            selectedMeeting={selectedMeeting}
          />
        )}
      </Main>
      <Details>
        {!isMeetingDetailsOpen && !isAddNewMeetingOpen && !isJoinMeetingByCodeOpen && (
          activeTab === "upcoming" ? (
            <WelcomeWrapper>
              <IconBadge>
                <CalendarRange />
              </IconBadge>
              <WelcomeTitle>{t('meetings.welcome_title')}</WelcomeTitle>
              <WelcomeSubtitle>{t('meetings.welcome_subtitle')}</WelcomeSubtitle>
              <FeaturesRow>
                <FeatureChip>
                  <Globe size={14} />
                  {t('meetings.feature_translation')}
                </FeatureChip>
                <FeatureChip>
                  <Sparkles size={14} />
                  {t('meetings.feature_ai_summary')}
                </FeatureChip>
              </FeaturesRow>
              <PrimaryButton
                onClick={() => handleOpenAddNewMeeting()}
                iconLeft={<CalendarPlus size={16} />}
                style={{ padding: "12px 22px", borderRadius: "8px" }}
              >
                {t('meetings.schedule_a_meeting')}
              </PrimaryButton>
            </WelcomeWrapper>
          ) : (
            <WelcomeWrapper>
              <IconBadge>
                <CalendarRange />
              </IconBadge>
              <WelcomeTitle>{t('meetings.past_placeholder_title')}</WelcomeTitle>
              <WelcomeSubtitle>{t('meetings.select_meeting_to_view_details')}</WelcomeSubtitle>
            </WelcomeWrapper>
          )
        )}

        {isMeetingDetailsOpen && (
          <MeetingDetailsCard
            meeting={selectedMeeting}
            handleCloseMeetingDetails={handleCloseMeetingDetails}
            handleDeleteMeetingBtn={(meeting) =>
              handleDeleteMeetingBtn(
                meeting,
                activeTab === "past"
                  ? () => loadMeetingsHistory({ reset: true })
                  : undefined
              )
            }
            handleUpdateMeetingDetails={handleUpdateMeetingDetails}
            handleRegeneratePasscode={handleRegeneratePasscode}
            contacts={contacts}
            handleJoinMeeting={handleJoinMeeting}
          />
        )}
        {isAddNewMeetingOpen && (
          <AddNewMeetingModal
            contacts={contacts}
            loadMeetings={loadMeetings}
            checkSubscriptionAndExecute={checkSubscriptionAndExecute}
          />
        )}
        {isJoinMeetingByCodeOpen && <JoinMeetingModal />}
      </Details>
    </Container>
  );
};

export default Meetings;

const Container = styled.div`
  display: flex;
  width: 100%;
  position: relative;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 419px;
  width: 100%;
  background: ${({ theme }) => theme.menu.background};
  border-right: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 19px 16px;
  align-items: center;
  background: ${({ theme }) => theme.menu.background};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  gap: 20px;
`;
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
const HeaderTitle = styled.h3`
  font-weight: 700;
  font-size: 20px;
  line-height: 32px;
  letter-spacing: 0.04px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;
const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin: 12px 16px 0 16px;
  padding: 4px;
  border-radius: 8px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
`;

const TabButton = styled.button`
  flex: 1;
  padding: 8px 0;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ theme, $active }) =>
    $active ? theme.chat.selectedBackground : "transparent"};
  color: ${({ theme, $active }) =>
    $active ? theme.menu.selectedTextColor : theme.menu.secondaryTextColor};
`;

const Details = styled.div`
  width: 100%;
  display: flex;
  background: ${({ theme }) => theme.menu.background};
`;

const WelcomeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  margin: auto;
  max-width: 440px;
  text-align: center;
`;

const IconBadge = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: ${({ theme }) => theme.chat.selectedBackground};
  box-shadow: 0 8px 24px rgba(${rgb(colors.emotiiPurple)}, 0.25);

  svg {
    width: 28px;
    height: 28px;
  }
`;

const WelcomeTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  margin: 0;
`;

const WelcomeSubtitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  margin: 0 0 4px 0;
`;

const FeaturesRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
`;

const FeatureChip = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  font-size: 12px;
  font-weight: 500;
`;
